import { startCase } from 'lodash-es'

/** The dialog's value picker — geometry and rows, with no DOM rendering and no component. */

/**
 * One selectable value, normalized from a static option or a resolved row (PVAL-26685) — see
 * `normalizeOptions`.
 */
export interface ValuePickerOption {
  /** Written to the field on pick — the row's `valueKey`. */
  value: string
  /** What the closed control shows once picked — the row's `displayText`. */
  text: string
  /** One entry per `displayColumns` column, in order. A column the row lacks is `''`. */
  cells: string[]
}

/** One rendered row of the panel. */
export interface ValuePickerRow {
  value: string
  text: string
  /** One per header column; a single-column list renders `text` alone, as it always did. */
  cells: string[]
  title: string
  selected: boolean
  /** The "Clear" row — an action, not a value, and inked as one. */
  clear: boolean
}

export interface ValuePickerGeometry {
  left: number
  top: number
  width: number
}

/** A `DOMRect`, narrowed to the fields the geometry reads, so it can be tested with plain objects. */
export interface ValuePickerRect {
  left: number
  top: number
  bottom: number
  width: number
  height: number
}

/** One row height for the panel, from the design. */
export const PICKER_ROW_HEIGHT = 32

/** Five rows is the cap; the sixth value is reached by scrolling, not by a taller panel. */
export const PICKER_VISIBLE_ROWS = 5

export const PICKER_MAX_HEIGHT = PICKER_ROW_HEIGHT * PICKER_VISIBLE_ROWS + 2

/** Narrower than this and a value is ellipsised before the cell is, so the panel stops shrinking. */
const PICKER_MIN_WIDTH = 170

/**
 * What a multi-column list's columns are sized from. The panel is placed before it is drawn, so there
 * is nothing to measure yet: a column is as wide as its longest header or value at roughly this many
 * px per character (12px text; letters and digits run 6-8px), plus the gap `.vp-cell` leaves after
 * it. The estimate errs wide on purpose — a column a little too wide costs nothing, and one a little
 * too narrow is the ellipsis this exists to remove.
 */
const PICKER_CHAR_WIDTH = 7.5
const PICKER_CELL_GAP = 8
const PICKER_MIN_COLUMN = 48

/** Past this a value is ellipsised (its whole text is the row's `title`), so one long value cannot make a page. */
const PICKER_MAX_COLUMN = 280

/** The row's side padding (2 x 9) and a vertical scrollbar, which takes its width from the cells. */
const PICKER_PANEL_CHROME = 34

/** Breathing room kept between the panel and the dialog's own edges. */
const EDGE_GAP = 6

/** The hook {@link ValuePicker.dismiss} looks for. */
export const PICKER_TRIGGER_CLASS = 'vp-trigger'

/**
 * The panels' own elements, by tag: `closest` matches a component selector like any other.
 *
 * Both are exempt from the dismissal rule — a press inside a panel is the panel's business, and the
 * calendar's month and year selects are presses inside one.
 */
const PANEL_SELECTORS = ['hs-value-picker']

/** The options a field offers, with the value it currently holds guaranteed present. */
export function pickerOptions(
  options: readonly ValuePickerOption[],
  value: string,
): ValuePickerOption[] {
  const listed = options.map(option => ({
    value: option.value,
    text: option.text || option.value,
    cells: option.cells,
  }))
  if (!value || listed.some(option => option.value === value)) return listed
  return [{ value, text: value, cells: [value] }, ...listed]
}

/**
 * What the trigger shows: the `text` of the option it holds, or the value itself when no loaded
 * option matches — a stored id whose row has not been fetched still reads as that id.
 */
export function pickerLabel(
  options: readonly ValuePickerOption[],
  value: string,
): string {
  if (!value) return ''
  const match = options.find(option => option.value === value)
  return match ? match.text || match.value : value
}

/** The panel's rows for a field. */
export function pickerRows(
  options: readonly ValuePickerOption[],
  value: string,
): ValuePickerRow[] {
  const rows: ValuePickerRow[] = value
    ? [
        {
          value: '',
          text: 'Clear',
          cells: [],
          title: '',
          selected: false,
          clear: true,
        },
      ]
    : []

  for (const option of pickerOptions(options, value)) {
    rows.push({
      value: option.value,
      text: option.text,
      cells: option.cells,
      title:
        option.cells.length > 1
          ? option.cells.filter(Boolean).join(' — ')
          : option.text === option.value
          ? option.value
          : option.text + ' — ' + option.value,
      selected: option.value !== '' && option.value === value,
      clear: false,
    })
  }

  return rows
}

/**
 * A column's header: `startCase` of the last dotted segment — `GLAccountSubType` reads "GL Account
 * Sub Type", `security.ticker` reads "Ticker".
 *
 * Not the repo's `toTitleCase`, which spaces every capital and would print "G L Account Sub Type".
 */
export const pickerColumnHeader = (column: string): string =>
  startCase(column.split('.').pop() ?? column)

/**
 * The width, in px, each column of a multi-column list wants: the longest of its header and its
 * values, estimated from character counts and kept between a floor and a ceiling.
 *
 * The header and every row lay out on these same widths, so a column's values sit under its name.
 */
export function pickerColumnWidths(
  columns: readonly string[],
  rows: readonly ValuePickerRow[],
): number[] {
  return columns.map((column, index) => {
    const longest = rows.reduce(
      (most, row) => Math.max(most, (row.cells[index] ?? '').length),
      pickerColumnHeader(column).length,
    )
    return Math.min(
      PICKER_MAX_COLUMN,
      Math.max(
        PICKER_MIN_COLUMN,
        Math.ceil(longest * PICKER_CHAR_WIDTH) + PICKER_CELL_GAP,
      ),
    )
  })
}

/** The panel width a multi-column list wants, or null for a single column, which follows its cell. */
function pickerFitWidth(
  columns: readonly string[],
  rows: readonly ValuePickerRow[],
): number | null {
  if (columns.length < 2) return null
  return pickerColumnWidths(columns, rows).reduce(
    (sum, width) => sum + width,
    PICKER_PANEL_CHROME,
  )
}

/** Where the panel hangs, in the coordinate space of `root` — the dialog, not the scroll box. */
/**
 * How big a panel is, which is the only thing that differs between them.
 *
 * The value list follows the width of the cell it hangs off; the calendar is a fixed 218 x 236
 * because a month grid has a size of its own. Everything else about the anchoring — the edge gaps,
 * the flip-above rule, the one-pixel back-off — is shared, which is what keeps the two panels
 * landing in the same place relative to their cells.
 */
export interface PanelMetrics {
  /** A fixed width, or null to follow the cell. */
  width: number | null
  /** Narrower than this and a value is ellipsised before the cell is. */
  minWidth: number
  /** What the panel needs below the cell before it flips above instead. */
  maxHeight: number
  /**
   * The width the CONTENT wants, when that is more than the cell gives. A multi-column lookup (the
   * instrument search shows six columns) wants far more than the cell it hangs off, and equal shares
   * of the cell's width ellipsise every column. The panel is at least this wide, within the viewport.
   */
  fitWidth?: number
}

/** The value list: as wide as its cell, floored, and five rows tall. */
export const VALUE_PANEL_METRICS: PanelMetrics = {
  width: null,
  minWidth: PICKER_MIN_WIDTH,
  maxHeight: PICKER_MAX_HEIGHT,
}

/**
 * Where a panel hangs, in the coordinate space of `root` — the dialog, not the scroll box.
 *
 * Anchored to the cell rather than dropped at the pointer, so it reads as that cell's panel however
 * the grid is scrolled. Three rules, all the prototype's `lookPos` / `calPos`:
 *
 *  - **width** is the panel's own when it has one (the calendar) and the cell's when it has not
 *    (the value list) — or what a multi-column list's content wants, when that is more — floored so
 *    a narrow column still shows its values and capped so the panel cannot reach past the screen;
 *  - **top** sits on the cell's bottom edge, and flips above the cell when the panel would
 *    otherwise run off the bottom of the dialog;
 *  - **left** backs off one pixel. The rule to a cell's left belongs to the cell before it, so the
 *    cell's own rect starts one pixel inside it; backing off that pixel puts the panel's border ON
 *    the line rather than beside it.
 *
 * `rootBorder` is the root's own border width (`clientLeft` / `clientTop`). It sits inside the
 * root's bounding rect but outside the coordinate space an absolutely positioned child measures
 * from, so it comes back out of both offsets.
 */
export function pickerGeometry(
  cell: ValuePickerRect,
  root: ValuePickerRect,
  rootBorder: { left: number; top: number },
  metrics: PanelMetrics = VALUE_PANEL_METRICS,
  viewport: { width: number; height: number } = {
    width: root.width,
    height: root.height,
  },
): ValuePickerGeometry {
  // Decided in VIEWPORT space and converted at the end. The dialog no longer clips these panels
  // (see `openBulkTransactionModal`), so what matters is whether the panel fits on the SCREEN below
  // its cell — not whether it fits inside the dialog. Asking the dialog is what sent a 236px
  // calendar to the top of a short modal and onto its own header.
  const width = Math.max(
    metrics.minWidth,
    Math.min(
      Math.max(
        Math.round(metrics.width ?? cell.width),
        Math.round(metrics.fitWidth ?? 0),
      ),
      viewport.width - EDGE_GAP * 2,
    ),
  )

  let top = cell.bottom
  if (top + metrics.maxHeight > viewport.height - EDGE_GAP) {
    // Flip only when above genuinely has more room. Overflowing the dialog is free; detaching the
    // panel from the cell it belongs to is not.
    const roomBelow = viewport.height - cell.bottom
    const roomAbove = cell.top
    if (roomAbove > roomBelow) {
      top = Math.max(EDGE_GAP, cell.top - metrics.maxHeight)
    }
  }

  // The rule to a cell's left belongs to the cell before it, so the cell's own rect starts one
  // pixel inside it; backing off that pixel puts the panel's border ON the line, not beside it.
  const left = Math.max(
    EDGE_GAP,
    Math.min(cell.left - 1, viewport.width - width - EDGE_GAP),
  )

  return {
    left: left - root.left - rootBorder.left,
    top: top - root.top - rootBorder.top,
    width,
  }
}

/**
 * An open anchored panel, or the absence of one.
 *
 * Holds everything the value list and the calendar agree on: which cell is open, where the panel
 * sits, re-measuring on scroll, and the dismissal rule. The two subclasses add only what they
 * actually differ in — a list of rows, or a month being browsed.
 *
 * `dismiss` is a document listener rather than a scrim. The sticky header row and the cells sit in
 * their own stacking contexts, so a scrim at the dialog either paints over the trigger — stealing
 * its cursor and its click — or under the panel. A document listener covers both and covers nothing.
 */
export abstract class AnchoredPanel {
  /** Which cell's panel is open, or null when none is. */
  private key: string | null = null

  /** The cell the panel hangs off, re-measured on scroll. */
  private anchor: HTMLElement | null = null

  /**
   * The dialog the panel belongs to. It positions nothing: the panel is a child of `body`, placed in
   * viewport coordinates. It is only what a cell has to stay inside for the panel to stay open.
   */
  private root: HTMLElement | null = null

  /** Viewport coordinates: the panel is `position: fixed` on `body`. */
  left = 0
  top = 0
  width = 0

  /**
   * Re-measures on any scroll or resize while a panel is open. The panel is on `body`, so nothing
   * moves it along with its cell — not the dialog scrolling, not the window resizing.
   */
  private readonly follow = (): void => this.reanchor()

  /** How big this panel is. The one thing the subclasses differ on geometrically. */
  protected abstract metrics(): PanelMetrics

  /** Called when the panel closes, so a subclass can drop whatever it was holding. */
  protected onClose(): void {
    // Nothing by default.
  }

  get open(): boolean {
    return this.key !== null
  }

  /** Whether the panel currently open belongs to this key. Drives a trigger's open/shut glyph. */
  isOpen(key: string): boolean {
    return this.key === key
  }

  /** True when this key's panel was already open, so a caller can treat the press as a toggle. */
  protected openAt(
    key: string,
    anchor: HTMLElement,
    root: HTMLElement,
  ): boolean {
    if (this.key === key) {
      this.close()
      return false
    }
    this.key = key
    this.anchor = anchor
    this.root = root
    this.measure()
    // Scroll does not bubble, so only the capture phase hears every box that scrolls. Registering the
    // same listener again, for a panel that moves to another cell, is ignored by the DOM.
    window.addEventListener('scroll', this.follow, true)
    window.addEventListener('resize', this.follow)
    return true
  }

  close(): void {
    window.removeEventListener('scroll', this.follow, true)
    window.removeEventListener('resize', this.follow)
    this.key = null
    this.anchor = null
    this.root = null
    this.onClose()
  }

  /**
   * Re-measure after the grid scrolls.
   *
   * The panel is anchored to its own cell, so a scroll moves the cell out from under it. Closing on
   * scroll instead would dismiss a panel the user had only just opened: focusing a partly-visible
   * cell makes the browser scroll it into view. So this re-anchors, and only gives up once the cell
   * has left the dialog — or left the document.
   */
  reanchor(): void {
    if (!this.anchor || !this.root) return
    if (!this.anchor.isConnected) {
      this.close()
      return
    }
    const cell = this.anchor.getBoundingClientRect()
    const root = this.root.getBoundingClientRect()
    if (cell.bottom < root.top || cell.top > root.bottom) {
      this.close()
      return
    }
    this.measure()
  }

  /**
   * Close on a press outside, unless it landed on a panel or on a trigger.
   *
   * A trigger is exempt because its OWN handler runs first and has already decided: the same press
   * toggles this cell shut, or opens another cell's panel in place of this one.
   */
  dismiss(target: EventTarget | null): void {
    if (!this.open) return
    if (
      target instanceof Element &&
      target.closest(
        '.' + PICKER_TRIGGER_CLASS + ', ' + PANEL_SELECTORS.join(', '),
      )
    ) {
      return
    }
    this.close()
  }

  protected measure(): void {
    if (!this.anchor || !this.root) return
    // Measured against the viewport itself — it starts at (0, 0) and has no border — because the
    // panel is a child of `body`, not of the dialog (`ValuePickerComponent` moves it there), so the
    // dialog's own edges neither clip it nor are its origin.
    const viewport = { width: window.innerWidth, height: window.innerHeight }
    const geometry = pickerGeometry(
      this.anchor.getBoundingClientRect(),
      { left: 0, top: 0, bottom: viewport.height, ...viewport },
      { left: 0, top: 0 },
      this.metrics(),
      viewport,
    )
    this.left = geometry.left
    this.top = geometry.top
    this.width = geometry.width
  }
}

/** The value list — a closed set of options, anchored to the cell that holds one of them. */
export class ValuePicker extends AnchoredPanel {
  rows: readonly ValuePickerRow[] = []

  /**
   * The open list's `displayColumns` (PVAL-26685). The panel draws a header over them only when
   * there is more than one — a one-column list is a value column and needs no name.
   */
  columns: readonly string[] = []

  /**
   * The width the open list's content wants, or null when it follows its cell (a single column).
   * Read by {@link metrics}.
   */
  private fitWidth: number | null = null

  /** Open this cell's list, or shut it if this cell's list is the one already open. */
  toggle(
    key: string,
    options: readonly ValuePickerOption[],
    value: string,
    anchor: HTMLElement,
    root: HTMLElement,
    columns: readonly string[] = [],
  ): void {
    // Set BEFORE opening: `openAt` measures, and the panel's width is read off these rows. Shutting
    // the list clears them again, so a toggle that closes leaves nothing behind.
    this.rows = pickerRows(options, value)
    this.columns = columns
    this.fitWidth = pickerFitWidth(columns, this.rows)
    this.openAt(key, anchor, root)
  }

  /**
   * Open this cell's list, or REPLACE its rows if it is already open — never a toggle.
   *
   * What a typeahead needs and {@link toggle} cannot give it: each keystroke's results arrive while
   * the panel is already open for that cell, and toggling would shut it on the second keystroke.
   *
   * `clear: false` — a search panel offers no Clear row. The text box IS the value, so emptying it
   * is how a lookup is cleared, and a row that did it from inside the list would be a second way
   * to do the same thing.
   */
  show(
    key: string,
    options: readonly ValuePickerOption[],
    anchor: HTMLElement,
    root: HTMLElement,
    columns: readonly string[] = [],
  ): void {
    const rows = pickerRows(options, '')
    const fit = pickerFitWidth(columns, rows)
    // Results arrive per keystroke while the panel stays open, so it never narrows within one
    // opening: a panel that resized with every answer would jump under the cursor.
    this.fitWidth =
      this.isOpen(key) && this.fitWidth != null && fit != null
        ? Math.max(this.fitWidth, fit)
        : fit
    this.rows = rows
    this.columns = columns
    if (!this.isOpen(key)) this.openAt(key, anchor, root)
    else this.measure()
  }

  protected metrics(): PanelMetrics {
    // A table carries a header row above its five, which the flip-above rule has to leave room for:
    // on `body` there is no dialog edge to hide the overhang, so one that did not fit hung off the
    // bottom of the screen.
    const base: PanelMetrics =
      this.columns.length > 1
        ? {
            ...VALUE_PANEL_METRICS,
            maxHeight: VALUE_PANEL_METRICS.maxHeight + PICKER_ROW_HEIGHT,
          }
        : VALUE_PANEL_METRICS
    return this.fitWidth == null ? base : { ...base, fitWidth: this.fitWidth }
  }

  protected onClose(): void {
    this.rows = []
    this.columns = []
    this.fitWidth = null
  }
}
